/**
 * negative-scanner / gl.js — the live viewfinder.
 *
 * The camera frame is uploaded as a texture and every screen pixel looks
 * itself up in the SAME 256-entry table the export uses (convert.js
 * buildLut): the viewfinder is not an approximation of the result, it is the
 * result at screen resolution. The only per-pixel arithmetic in the shader is
 * the table lookup, the luma for B&W and the saturation mix — the same three
 * steps applyLut() does on the CPU.
 *
 * `uM` maps output coordinates (0..1, top-left origin) to source coordinates,
 * which is how rotation and the emulsion-side mirror reach the preview
 * without touching the camera stream.
 *
 * When WebGL is missing or its context is lost, createCpuLive() draws the
 * same thing with drawImage + applyLut at a smaller size.
 */
import { applyLut } from './convert.js';

const VS = `
attribute vec2 aPos;
varying vec2 vOut;
void main() {
    vOut = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5);
    gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FS = `
precision highp float;
varying vec2 vOut;
uniform sampler2D uSrc;
uniform sampler2D uLut;
uniform mat3 uM;
uniform float uSat;
uniform float uBw;
uniform float uRaw;
float lut(float v, int ch) {
    vec4 t = texture2D(uLut, vec2((floor(v * 255.0 + 0.5) + 0.5) / 256.0, 0.5));
    return ch == 0 ? t.r : ch == 1 ? t.g : t.b;
}
void main() {
    vec3 s = uM * vec3(vOut, 1.0);
    vec2 st = s.xy / s.z;
    if (st.x < 0.0 || st.y < 0.0 || st.x > 1.0 || st.y > 1.0) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
    vec3 c = texture2D(uSrc, st).rgb;
    if (uRaw > 0.5) { gl_FragColor = vec4(c, 1.0); return; }
    if (uBw > 0.5) {
        float l = floor(dot(c * 255.0, vec3(54.0, 183.0, 19.0)) / 256.0 + 0.5) / 255.0;
        float y = lut(l, 0);
        gl_FragColor = vec4(y, y, y, 1.0);
        return;
    }
    vec3 o = vec3(lut(c.r, 0), lut(c.g, 1), lut(c.b, 2));
    float Y = dot(o, vec3(0.2126, 0.7152, 0.0722));
    o = clamp(vec3(Y) + (o - vec3(Y)) * uSat, 0.0, 1.0);
    gl_FragColor = vec4(o, 1.0);
}`;

/**
 * Output-to-source matrix for rotation (clockwise, degrees) and mirror.
 * Column-major Float32Array(9) as WebGL wants it.
 */
export function orientMatrix(rot, flip) {
    rot = ((rot || 0) % 360 + 360) % 360;
    // out (x, y) → after undoing the mirror → after undoing the rotation
    // s = a*x + b*y + c ; t = d*x + e*y + f
    let a, b, c, d, e, f;
    const fx = flip ? -1 : 1, fc = flip ? 1 : 0;   // x' = fx*x + fc
    if (rot === 0) { a = fx; b = 0; c = fc; d = 0; e = 1; f = 0; }
    else if (rot === 90) { a = 0; b = 1; c = 0; d = -fx; e = 0; f = 1 - fc; }
    else if (rot === 180) { a = -fx; b = 0; c = 1 - fc; d = 0; e = -1; f = 1; }
    else { a = 0; b = -1; c = 1; d = fx; e = 0; f = fc; }
    return new Float32Array([a, d, 0, b, e, 0, c, f, 1]);
}

/** Apply the same mapping on the CPU (for taps on the preview). */
export function outToSrcNorm(rot, flip, x, y) {
    const m = orientMatrix(rot, flip);
    return { x: m[0] * x + m[3] * y + m[6], y: m[1] * x + m[4] * y + m[7] };
}

export function createGlLive(canvas) {
    let gl = null;
    try {
        const o = { premultipliedAlpha: false, antialias: false, preserveDrawingBuffer: true, alpha: false };
        gl = canvas.getContext('webgl', o) || canvas.getContext('experimental-webgl', o);
    } catch (_) { gl = null; }
    if (!gl) return null;

    const sh = (type, src) => {
        const s = gl.createShader(type);
        gl.shaderSource(s, src); gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error('shader: ' + gl.getShaderInfoLog(s));
        return s;
    };
    let prog;
    try {
        let fs = FS;
        const hp = gl.getShaderPrecisionFormat && gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
        if (!hp || !hp.precision) fs = fs.replace('precision highp float;', 'precision mediump float;');
        prog = gl.createProgram();
        gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
        gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, fs));
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
    } catch (_) { return null; }
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
    const U = (n) => gl.getUniformLocation(prog, n);
    const uM = U('uM'), uSat = U('uSat'), uBw = U('uBw'), uRaw = U('uRaw');
    gl.uniform1i(U('uSrc'), 0);
    gl.uniform1i(U('uLut'), 1);

    const tex = (filter) => {
        const t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return t;
    };
    const srcTex = tex(gl.LINEAR), lutTex = tex(gl.NEAREST);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, lutTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(1024).fill(255));

    let lost = false;
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); lost = true; }, false);

    return {
        kind: 'gl',
        get lost() { return lost || (gl.isContextLost && gl.isContextLost()); },
        setLut(lut) {
            if (lost) return;
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, lutTex);
            gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 256, 1, gl.RGBA, gl.UNSIGNED_BYTE, lut);
        },
        /** Draw one frame of `src` (a <video>, <canvas> or ImageBitmap). */
        draw(src, o) {
            if (lost) return false;
            const W = canvas.width, H = canvas.height;
            gl.viewport(0, 0, W, H);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, srcTex);
            try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src); } catch (_) { return false; }
            gl.uniformMatrix3fv(uM, false, orientMatrix(o.rot, o.flip));
            gl.uniform1f(uSat, o.sat == null ? 1 : o.sat);
            gl.uniform1f(uBw, o.bw ? 1 : 0);
            gl.uniform1f(uRaw, o.raw ? 1 : 0);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            return true;
        },
        dispose() {
            try { gl.deleteTexture(srcTex); gl.deleteTexture(lutTex); gl.deleteBuffer(buf); gl.deleteProgram(prog); } catch (_) {}
        },
    };
}

/** The same viewfinder without WebGL: smaller, on the CPU, same table. */
export function createCpuLive(canvas) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    let lut = new Uint8Array(1024).fill(255);
    return {
        kind: 'cpu',
        lost: false,
        setLut(l) { lut = l; },
        draw(src, o) {
            const W = canvas.width, H = canvas.height;
            const sw = src.videoWidth || src.width, sh = src.videoHeight || src.height;
            if (!sw || !sh) return false;
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            // out → src is orientMatrix; draw with its inverse (src → out)
            const m = orientMatrix(o.rot, o.flip);
            // m maps (x,y,1) → (s,t,1) in normalised units; invert the 2×2 affine
            const a = m[0], b = m[3], c = m[6], d = m[1], e = m[4], f = m[7];
            const det = a * e - b * d;
            const ia = e / det, ib = -b / det, id = -d / det, ie = a / det;
            const ic = -(ia * c + ib * f), iff = -(id * c + ie * f);
            // src px → normalised (/sw, /sh) → out normalised → out px (*W, *H)
            ctx.setTransform(ia * W / sw, id * H / sw, ib * W / sh, ie * H / sh, ic * W, iff * H);
            ctx.drawImage(src, 0, 0, sw, sh);
            ctx.restore();
            if (!o.raw) {
                const img = ctx.getImageData(0, 0, W, H);
                applyLut(img.data, img.data, lut, !!o.bw, o.sat == null ? 1 : o.sat);
                ctx.putImageData(img, 0, 0);
            }
            return true;
        },
        dispose() {},
    };
}
